// @ts-strict-ignore
import {ChangeDetectionStrategy, Component, Injector} from "@angular/core";
import {BasicMaterialDialog, MatDialogDataBaseInput} from "../../basic-material-dialog/basic-material-dialog.directive";
import {
	ActivityState,
	ConnectivityState,
	PlayerMapping,
	PlayerStatus,
	RecorderMapping,
	RoomCapability,
	TrainingRoom,
	TrainingRoomType,
} from "@models";
import {
	SimClientRegisterService,
	SimMaterialDialogService,
	NotificationService,
	SimRecorderService,
	SimSystemService,
	SimTrainingRoomService,
	SimUtilityService,
} from "@services";
import {TranslateService} from "@ngx-translate/core";
import {Translations} from "@translations";
import Enumerable from "linq";
import {animate, query, stagger, style, transition, trigger} from "@angular/animations";
import {zoomInOnEnterAnimation, zoomOutOnLeaveAnimation} from "angular-animations";
import {EventEmitterService} from "@event-emitter";

export interface DialogDataAddDebriefingPlayerDialog extends MatDialogDataBaseInput {
	players: PlayerMapping[];
	busyPlayers: string[];

	// The recorder for which the players are selected
	recorderMappingId: string;
	selectedPlayerIds: string[];
}

export interface DialogDataAddDebriefingPlayerDialogOutput {
	action: "ok" | "cancel";
	selectedPlayerIds: string[];
}

type PlayerStatusClass = "" | "offline" | "busy" | "ready" | "recording";

@Component({
	selector: "sim-select-players-dialog",
	templateUrl: "./select-players-dialog.component.html",
	styleUrls: ["./select-players-dialog.component.scss"],
	providers: [EventEmitterService],
	animations: [
		zoomInOnEnterAnimation({duration: 175}),
		zoomOutOnLeaveAnimation({duration: 175}),
		trigger("listStagger", [
			transition("* <=> *", [
				query(
					":enter",
					[
						style({opacity: 0, transform: "translateY(-15px)"}),
						stagger(
							"50ms",
							animate(
								"550ms ease-out",
								style({
									opacity: 1,
									transform: "translateY(0px)",
								}),
							),
						),
					],
					{optional: true},
				),
				query(":leave", animate("0ms", style({opacity: 0})), {
					optional: true,
				}),
			]),
		]),
	],
	changeDetection: ChangeDetectionStrategy.Default,
	standalone: false,
})
export class SelectPlayersDialogComponent extends BasicMaterialDialog<
	SelectPlayersDialogComponent,
	DialogDataAddDebriefingPlayerDialog,
	DialogDataAddDebriefingPlayerDialogOutput
> {
	T = Translations;
	PlayerStatus = PlayerStatus;
	ConnectivityState = ConnectivityState;
	ActivityState = ActivityState;

	public selectedIndex = 0;
	public liveStreamRooms: TrainingRoom[] = [];
	public loadingRooms: boolean = true;
	public selectedRooms: Set<string> = new Set<string>();

	// Map of recorderMappingId to recorderMapping
	private _recorderMappings: Map<string, RecorderMapping> = new Map<string, RecorderMapping>();
	// Map of roomId to recorderMappingId
	private _selectedRoomsByOthers: Map<string, string> = new Map<string, string>();

	constructor(
		protected _simStationRecorderProxyService: SimRecorderService,
		protected _NotificationService: NotificationService,
		private _translate: TranslateService,
		protected _injector: Injector,
		private _simStationRoomService: SimTrainingRoomService,
		private _eventEmitterService: EventEmitterService,
		private _simSystemService: SimSystemService,
		private _simMaterialDialogService: SimMaterialDialogService,
		private _simClientRegisterService: SimClientRegisterService,
	) {
		super(_injector);

		this._simSystemService.getRecorderMappings().then((recorderMappings) => {
			recorderMappings.forEach((recorderMapping) => {
				this._recorderMappings.set(recorderMapping.id, recorderMapping);
			});
		});

		SimUtilityService.promiseTakeAtLeast(
			500,
			this._simStationRoomService.getTrainingRoomsWithFilter(TrainingRoomType.Physical, RoomCapability.Debriefing).then((roomRes) => {
				this.liveStreamRooms = roomRes;
				this.determineSelectedRooms();
			}),
		).then(() => {
			this.loadingRooms = false;
		});

		this._eventEmitterService.onPlayerStateChanged.subscribe((args) => {
			if (args.player) {
				if (this.liveStreamRooms && this.liveStreamRooms?.length > 0) {
					this.liveStreamRooms.forEach((liveStreamRoom) => {
						const playerToUpdate = Enumerable.from(liveStreamRoom.debriefingPlayers).firstOrDefault((player) => {
							return player.id === args.player.id;
						});

						if (playerToUpdate) {
							Object.assign(playerToUpdate, args.player);
						}
					});
				}
			}
		});

		this._eventEmitterService.onWebRecorderStateChanged.subscribe(({recorder}) => {
			if (recorder) {
				this._recorderMappings.set(recorder.id, recorder);
				this.determineSelectedRooms();
			}
		});
	}

	public saveConfig() {
		let selectedPlayerIds = [];
		this.liveStreamRooms
			.filter((liveStreamRoom) => this.selectedRooms.has(liveStreamRoom.id))
			.forEach((value) => (selectedPlayerIds = selectedPlayerIds.concat(value.debriefingPlayerDeviceIds)));

		this.close({
			action: "ok",
			selectedPlayerIds: [...new Set(selectedPlayerIds)],
		});
	}

	private determineSelectedRooms() {
		this._selectedRoomsByOthers.clear();
		this.fillSelectedRooms();
		this.fillRoomsUsedByOthers();
	}

	private fillRoomsUsedByOthers() {
		const remainingRooms = this.liveStreamRooms.filter((room) => !this.selectedRooms.has(room.id));
		for (const [_, recorderMapping] of this._recorderMappings) {
			// Skip current recorder
			if (recorderMapping.id === this.data.recorderMappingId) {
				continue;
			}

			// in the info object is up-to-date information about the selected players
			if (recorderMapping.info.selectedPlayerIds) {
				if (recorderMapping.webSocket.activityState !== ActivityState.Active) {
					continue;
				}

				for (const selectedPlayerId of recorderMapping.info.selectedPlayerIds) {
					for (const liveStreamRoom of remainingRooms) {
						if (this.playerIsInRoom(selectedPlayerId, liveStreamRoom)) {
							this._selectedRoomsByOthers.set(liveStreamRoom.id, recorderMapping.id);
						}
					}
				}
			}
		}
	}

	private fillSelectedRooms() {
		if (!this.loadingRooms) {
			return;
		}
		const selectedPlayers = [...this.data.selectedPlayerIds, ...this.data.busyPlayers];
		this.liveStreamRooms.forEach((liveStreamRoom) => {
			if (selectedPlayers.some((playerId) => this.playerIsInRoom(playerId, liveStreamRoom))) {
				this.selectedRooms.add(liveStreamRoom.id);
			}
		});
	}

	private playerIsInRoom(playerId: string, liveStreamRoom: TrainingRoom) {
		return liveStreamRoom.debriefingPlayers.some((liveStreamRoomPlayer) => liveStreamRoomPlayer.id === playerId);
	}

	isRoomInUse(liveStreamRoom: TrainingRoom) {
		return this._selectedRoomsByOthers.has(liveStreamRoom.id);
	}

	getRoomInUseByRecorder(liveStreamRoom: TrainingRoom) {
		const mappingId = this._selectedRoomsByOthers.get(liveStreamRoom.id);
		return this._recorderMappings.get(mappingId);
	}

	isRoomSelected(liveStreamRoom: TrainingRoom) {
		return this.selectedRooms.has(liveStreamRoom.id);
	}

	async liveStreamRoomClicked(liveStreamRoom: TrainingRoom) {
		if (this.isRoomInUse(liveStreamRoom)) {
			this._NotificationService.notifyInfo(this._translate.instant(this.T.PLAYER_ALREADY_IN_USE_BY_OTHER_DEVICE));
			return;
		}

		if (this.isRoomSelected(liveStreamRoom)) {
			this.selectedRooms.delete(liveStreamRoom.id);
		} else {
			let playersDebriefing = liveStreamRoom.players.filter(
				(player) =>
					[PlayerStatus.Debriefing, PlayerStatus.Preparing].includes(player.info.status) &&
					player.webSocket.activityState === ActivityState.Active,
			);
			if (playersDebriefing.length > 0) {
				const takeOverPerformed = await this._simClientRegisterService.handlePlayerTakeOver(
					playersDebriefing.map((player) => player.id),
				);

				if (takeOverPerformed) {
					this.selectedRooms.add(liveStreamRoom.id);
				}
			} else {
				this.selectedRooms.add(liveStreamRoom.id);
			}
		}
	}

	getPlayerStatusClass(liveStreamRoom: TrainingRoom): PlayerStatusClass {
		let generatedCssClass: PlayerStatusClass = "";

		let activePlayers = liveStreamRoom.debriefingPlayers.filter((player) => player.webSocket.activityState !== ActivityState.Inactive);

		if (activePlayers.length > 0) {
			activePlayers.forEach((player) => {
				switch (player.info.status) {
					case PlayerStatus.Debriefing:
					case PlayerStatus.Presentation:
						generatedCssClass = "busy";
						break;
					case PlayerStatus.Recording:
					case PlayerStatus.LiveStream:
					case PlayerStatus.PreparingTraining:
						generatedCssClass = "recording";
						break;
					case PlayerStatus.Preparing:
					case PlayerStatus.Ready:
						generatedCssClass = "ready";
						break;
					case PlayerStatus.Undefined:
					default:
						generatedCssClass = "offline";
						break;
				}
			});
		} else {
			generatedCssClass = "offline";
		}

		return generatedCssClass;
	}
}
